import { COST_SCOPE_REFS, CostScopeRef } from '../const/cost.const';
import {
  IApplicationComponent,
  IApplicationComponentRef,
  IApplicationFull,
  IApplicationFullResult,
  IApplicationParentRef,
  IApplicationScopeRef,
  IGroupedTotalCostComponent,
  IGroupedTotalCostComponents,
  IReferenceValue,
  ITotal,
  ITotalCostResults,
  ScopeResultKey,
} from '../types';
import { getMainApplicationFullResult } from '../utils';

// Generate a component with results from ITotal results
const getComponentsFromTotalResults = (
  totalResults: ITotal[][],
  componentRef: IApplicationComponentRef
): IApplicationComponent | undefined => {
  const results: ITotal[] | undefined = totalResults.find(
    (totalResult) => totalResult.find((v) => v.isMainResult)?.key === componentRef?.key
  );

  if (!results) {
    return undefined;
  }

  return {
    results: results.map((result) => {
      return {
        value: String(result.value),
        comment: typeof result.comments !== 'undefined' ? (result.comments.length === 1 ? result.comments[0] : '') : '',
        isMainResult: result.isMainResult,
        key: result.key,
        type: result.type || '',
        name: result.name,
        confidence: result.confidence,
        sources: result.sources,
        id: result.id,
        createdBy: '',
        createdDate: '',
        lastUpdate: '',
        lastUpdateBy: '',
        tbs: result.tbs ? result.tbs : undefined,
        chasingId: null,
      };
    }),
    versionRef: undefined,
    componentRef,
    recommendations: [],
    id: -1,
    createdBy: '',
    createdDate: '',
    lastUpdate: '',
    lastUpdateBy: '',
    scope: undefined,
  };
};

export const formatGroupedComponentWithTotalResult = (
  groupedComponent: IGroupedTotalCostComponents[],
  totalResults: ITotal[][]
): IGroupedTotalCostComponent[] => {
  return groupedComponent.map(({ componentsAggrByType, type }) => {
    return {
      componentsAggrByType: componentsAggrByType.map(({ componentRef, groupedResults }) => {
        const component = getComponentsFromTotalResults(totalResults, componentRef);

        return {
          components:
            component === undefined
              ? [undefined]
              : groupedResults.map((groupedResult) => {
                  if (groupedResult.groupedType === 'Sum') {
                    return { label: groupedResult.groupedType, results: component.results };
                  }

                  if (component.results.every((v) => v.tbs === undefined)) {
                    return { label: groupedResult.groupedType, results: [] };
                  }

                  const filteredTbs = component.results.filter((result) => !!result.tbs);

                  const componentResults = filteredTbs.flatMap((result) => {
                    const tbsLabel = Object.keys(result.tbs!);

                    return tbsLabel.map((value) => {
                      const totalResult = Object.values(result.tbs![value]).reduce((acc, curr) => acc + curr, 0);

                      return {
                        label: value.toUpperCase(),
                        result: totalResult,
                        key: result.key,
                      };
                    });
                  });

                  const [groupedTbs]: (ITotalCostResults | undefined)[] = componentResults
                    .filter((result) => groupedResult.groupedType === result.label)
                    .reduce((acc: ITotalCostResults[], curr) => {
                      const find = acc.find((result) => result.label === groupedResult.groupedType);
                      return find
                        ? [
                            {
                              label: find.label,
                              results: [
                                ...find.results.filter((value) => value.key !== curr.key),
                                {
                                  value: (
                                    Number(curr.result) +
                                    Number(find.results.find((value) => value.key === curr.key)?.value || 0)
                                  ).toString(),
                                  key: curr.key,
                                },
                              ],
                            },
                          ]
                        : [
                            {
                              label: groupedResult.groupedType,
                              results: [{ value: curr.result.toString(), key: curr.key }],
                            },
                          ];
                    }, []);

                  return groupedTbs ? groupedTbs : { label: groupedResult.groupedType, results: [] };
                }),
          componentRef,
        };
      }),
      type,
    };
  });
};

export const getComponentValueType = (
  componentRef: IApplicationComponentRef[],
  componentId: string,
  values: IReferenceValue[]
): IReferenceValue[] | undefined => {
  const costComponent = ['gts', 'azure', 'aws', 'worforce', 'software'];
  const targetedComponentRef = componentRef.find((component) => component.id === Number(componentId));

  if (costComponent.includes(targetedComponentRef ? targetedComponentRef.key : '')) {
    return values.filter((res) => ['value'].includes(res.key as string));
  }
  return values.filter((res) => ['rule', 'presence', 'value', 'percentage'].includes(res.key as string));
};

export const getScopeValueType = (
  scopeRef: IApplicationScopeRef[] | undefined,
  scopeId: string,
  values: IReferenceValue[]
): IReferenceValue[] | undefined => {
  if (typeof scopeRef === 'undefined') return;

  const targetedScopeRef = scopeRef.find((scope) => scope.id === Number(scopeId));

  if (COST_SCOPE_REFS.includes((targetedScopeRef ? targetedScopeRef.key : '') as CostScopeRef)) {
    return values.filter((res) => ['value'].includes(res.key as string));
  }
  return [...values].filter((res) => ['rule', 'value'].includes(res.key as string));
};

export function getResultKeyFromScopeRefKey(scopeKey: string): ScopeResultKey {
  // Some result keys are not corresponding to the scope keys
  const scopeKeyResultKeyMap: Record<string, ScopeResultKey> = {
    web: 'digital-capability-web',
    api: 'digital-capability-api',
    security: 'digital-capability-security',
    'ci-cd': 'digital-capability-ci-cd',
    monitoring: 'digital-capability-monitoring',
    users: 'max-users',
  };

  return (scopeKeyResultKeyMap[scopeKey] || scopeKey) as ScopeResultKey;
}

export function getParentRefKeyFromScopeRefKey(
  parentRefs: IApplicationParentRef[] | undefined,
  scopeKey: string
): string {
  if (!parentRefs) {
    return '';
  }
  return (
    parentRefs.find((parentRef) => parentRef.scopesRef.map((scopeRef) => scopeRef.key).includes(scopeKey))?.key || ''
  );
}

export function getResultLink(result: IApplicationFullResult): string | undefined {
  if (result.key.endsWith('sonar')) {
    return result.metadata?.sonar?.assetUrl;
  }
}

export function getApplicationName(application: IApplicationFull | undefined): string {
  return getMainApplicationFullResult(application?.global_taxon_iappliname)?.valueMutated ?? 'undefined';
}
